import { Inequality } from './cuts'
import { Fraction } from './fraction'
import { logger } from '@/app/logger'

export interface SimpleVertex {
  x: number
  y: number
  z: number
}

/**
 * Simple numeric vertex used at the API boundary. Internally we
 * immediately convert to Fractions so that V→H conversion can be
 * done with exact arithmetic, and then normalize all supporting
 * halfspaces to minimal integer form.
 */
export interface SimpleVertex {
  x: number
  y: number
  z: number
}

interface RationalVertex {
  x: Fraction
  y: Fraction
  z: Fraction
}

/**
 * Build supporting halfspaces (inequalities) for the convex hull of a set of vertices.
 * 3D only, purely arithmetic (no Three.js).
 *
 * The resulting inequalities all have the semantics: alpha · x <= beta
 * and every input vertex satisfies all returned inequalities.
 *
 * Implementation details:
 * - All calculations are performed exactly using Fractions.
 * - For every supporting plane we derive integer coefficients by
 *   clearing denominators and dividing by a global gcd, so each
 *   (alpha, beta) quadruple is in a canonical minimal integer form.
 * - This drastically reduces duplicate / scaled copies of the same
 *   facet and leads to “clean” H-descriptions even for rational
 *   vertices such as 0.5 or 0.6.
 */
export function buildInequalitiesFromVertices(vertices: SimpleVertex[]): Inequality[] {
  const n = vertices.length

  const planes = new Map<string, Inequality>()

  if (n < 4) {
    throw new Error(
      'Could not derive any supporting halfspaces from the provided vertices. Please ensure they form a 3D convex polyhedron (at least 4 points).',
    )
  }

  // Convert to exact rational vertices up-front.
  const verts: RationalVertex[] = vertices.map((v) => ({
    x: Fraction.fromDecimal(v.x),
    y: Fraction.fromDecimal(v.y),
    z: Fraction.fromDecimal(v.z),
  }))

  const getVec = (i: number) => verts[i]

  // Helper: cross product of two edge vectors
  const cross = (u: RationalVertex, v: RationalVertex): RationalVertex => ({
    x: u.y.multiply(v.z).subtract(u.z.multiply(v.y)),
    y: u.z.multiply(v.x).subtract(u.x.multiply(v.z)),
    z: u.x.multiply(v.y).subtract(u.y.multiply(v.x)),
  })

  // Helper: dot product
  const dot = (nVec: RationalVertex, p: RationalVertex): Fraction =>
    nVec.x.multiply(p.x).add(nVec.y.multiply(p.y)).add(nVec.z.multiply(p.z))

  const isZeroVector = (v: RationalVertex): boolean => v.x.isZero() && v.y.isZero() && v.z.isZero()

  const normalizePlaneToInteger = (nx: Fraction, ny: Fraction, nz: Fraction, beta: Fraction): { alpha: number[]; beta: number } => {
    // Collect denominators and build a common multiple so that all
    // scaled coefficients become integers.
    const dens = [
      nx.getDenominator(),
      ny.getDenominator(),
      nz.getDenominator(),
      beta.getDenominator(),
    ]

    let commonDen: bigint = 1n
    for (const d of dens) {
      commonDen = Fraction.lcm(commonDen, d)
    }

    const scale = (f: Fraction): bigint => {
      const num = f.getNumerator()
      const den = f.getDenominator()
      // commonDen is a multiple of den by construction.
      return (num * (commonDen / den))
    }

    const ints = [scale(nx), scale(ny), scale(nz), scale(beta)]
    const nonZero = ints.filter((v) => v !== 0n).map((v) => (v < 0n ? -v : v))

    if (nonZero.length === 0) {
      throw new Error('[VerticesToInequalities] Degenerate plane with all-zero coefficients encountered.')
    }

    // Compute gcd over |ints|
    let g = nonZero[0]
    const gcdBigInt = (a: bigint, b: bigint): bigint => {
      let x = a < 0n ? -a : a
      let y = b < 0n ? -b : b
      while (y !== 0n) {
        const t = y
        y = x % y
        x = t
      }
      return x
    }

    for (let idx = 1; idx < nonZero.length; idx++) {
      g = gcdBigInt(g, nonZero[idx])
    }

    if (g <= 0n) {
      g = 1n
    }

    const simplified = ints.map((v) => v / g)

    // Convert back to JS numbers. Inputs are expected to be small
    // rationals, so this is safe; if not, we log once for debugging.
    const toNum = (v: bigint): number => {
      const num = Number(v)
      if (!Number.isFinite(num)) {
        logger.warn('[VerticesToInequalities] Encountered non-finite coefficient when converting plane to number.', {
          value: v.toString(),
        })
      }
      return num
    }

    const finalAlpha = simplified.slice(0, 3).map(toNum)
    const finalBeta = toNum(simplified[3])

    return { alpha: finalAlpha, beta: finalBeta }
  }

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      for (let k = j + 1; k < n; k++) {
        const p = getVec(i)
        const q = getVec(j)
        const r = getVec(k)

        const e1: RationalVertex = {
          x: q.x.subtract(p.x),
          y: q.y.subtract(p.y),
          z: q.z.subtract(p.z),
        }
        const e2: RationalVertex = {
          x: r.x.subtract(p.x),
          y: r.y.subtract(p.y),
          z: r.z.subtract(p.z),
        }

        // Cross product e1 x e2 = normal
        let nVec = cross(e1, e2)

        if (isZeroVector(nVec)) {
          // Points are colinear or nearly so; skip this triple.
          continue
        }

        // Plane through p: n · x = beta
        let beta = dot(nVec, p)

        let hasPos = false
        let hasNeg = false

        for (let t = 0; t < n; t++) {
          if (t === i || t === j || t === k) continue
          const v = getVec(t)
          const s = dot(nVec, v).subtract(beta)
          if (s.isPositive()) hasPos = true
          else if (s.isNegative()) hasNeg = true
          if (hasPos && hasNeg) break
        }

        // If vertices lie on both sides, this is not a supporting plane
        if (hasPos && hasNeg) {
          continue
        }

        // Orient so that interior satisfies n·x <= beta
        if (hasPos && !hasNeg) {
          nVec = {
            x: nVec.x.negate(),
            y: nVec.y.negate(),
            z: nVec.z.negate(),
          }
          beta = beta.negate()
        }

        const normalized = normalizePlaneToInteger(nVec.x, nVec.y, nVec.z, beta)
        const finalAlpha = normalized.alpha
        const finalBeta = normalized.beta

        const key = `${finalAlpha.map((v) => v.toString()).join(',')}<=${finalBeta.toString()}`
        if (!planes.has(key)) {
          planes.set(key, { alpha: finalAlpha, beta: finalBeta })
        }
      }
    }
  }

  if (planes.size === 0) {
    throw new Error(
      'Could not derive any supporting halfspaces from the provided vertices. Please ensure they form a 3D convex polyhedron (at least 4 non-coplanar points).',
    )
  }

  return Array.from(planes.values())
}
